import { FormControl, FormHelperText, MenuItem, Select } from '@material-ui/core';
import en from '../../../../lang/en-us';
import TextInput from '../../../../lib/TextField';
import DatePickerInput from '../../../../lib/DatePicker';
import { checkForOnlyAlphabets, _minDateForCalender } from '../../../../utils/constants';
import { NOMINEEINFO } from './update-nominee-edit';
import { CheckBoxField } from '../../../../lib/CheckBoxField';
import { kfinRelations, camsRelations } from './addNominee';

const { dashboard } = en;

interface NomineeCardProps {
    index: number;
    nominee: NOMINEEINFO;
    isKfin: boolean;
    totalNominees: number;
    totalSharePercent: number;
    handleNomineeChange: (key: string, value: any, index: number) => void;
    handleRemoveNominee: (index: number) => void;
    onShareBlur: (index: number) => void;
    errors?: any;
}

/**
 * Redesigned Nominee Card Component
 * Shows only Name, Relationship, Share % with badge and minor checkbox
 * When minor is checked, shows DOB field inline
 */
function NomineeCard({
    index,
    nominee,
    isKfin,
    totalNominees,
    totalSharePercent,
    handleNomineeChange,
    handleRemoveNominee,
    onShareBlur,
    errors = {},
}: NomineeCardProps) {
    const handleChange = (key: string, value: any) => {
        handleNomineeChange(key, value, index);
    };

    const isMinor = nominee.isMinor || false;
    const showExceedsError = totalSharePercent > 100;

    return (
        <div className="nominee-card-new" data-nominee-card={index}>
            {/* Remove button - mobile only, at top right */}
            {totalNominees > 1 && index > 0 && (
                <button
                    type="button"
                    className="nominee-remove-btn nominee-remove-btn-mobile-top"
                    onClick={() => handleRemoveNominee(index)}
                >
                    <img src="/images/delete_nominee.svg" alt="remove" />
                    <span>Nom-{index + 1}</span>
                </button>
            )}

            {/* Main row: Badge + Name + Relationship + Share */}
            <div className="nominee-card-row">
                {/* Number badge */}
                <div className="nominee-badge-new">{index + 1}</div>

                {/* Full Name */}
                <div
                    className="nominee-field-new nominee-field-name"
                    data-nominee-index={index}
                    data-field-name="name"
                >
                    <label className="nominee-field-label-new">
                        {dashboard.UpdateNominee.fullName}{' '}
                        <span className="required-star">*</span>
                    </label>
                    <TextInput
                        variant="outlined"
                        placeholder={dashboard.UpdateNominee.fullNamePlaceholder}
                        value={nominee.name}
                        name="name"
                        onChange={(e: any) => {
                            if (checkForOnlyAlphabets(e.target.value) || !e.target.value) {
                                handleChange('name', e.target.value);
                            }
                        }}
                        error={!!errors.name}
                        helperText={errors.name}
                    />
                </div>

                {/* Relationship */}
                <div
                    className="nominee-field-new nominee-field-relationship"
                    data-nominee-index={index}
                    data-field-name="relation"
                >
                    <label className="nominee-field-label-new">
                        {dashboard.UpdateNominee.relationship}{' '}
                        <span className="required-star">*</span>
                    </label>
                    <FormControl variant="outlined" error={!!errors.relation} fullWidth>
                        <Select
                            value={nominee.relation || dashboard.UpdateNominee.selectRelation}
                            onChange={(e) => handleChange('relation', e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value={dashboard.UpdateNominee.selectRelation}>
                                {dashboard.UpdateNominee.selectRelation}
                            </MenuItem>
                            {(isKfin ? kfinRelations : camsRelations).map((relation) => (
                                <MenuItem value={relation} key={relation}>
                                    {relation}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.relation && (
                            <FormHelperText className="error">{errors.relation}</FormHelperText>
                        )}
                    </FormControl>
                </div>

                {/* Share % */}
                <div
                    className="nominee-field-new nominee-field-share"
                    data-nominee-index={index}
                    data-field-name="share"
                >
                    <div className="share-header">
                        <label className="nominee-field-label-new">
                            {dashboard.UpdateNominee.sharePercent}
                        </label>
                    </div>
                    <div className={`share-input-wrapper ${showExceedsError ? 'has-error' : ''}`}>
                        <input
                            type="text"
                            className="share-input"
                            value={nominee.share !== null && nominee.share !== undefined ? nominee.share : ''}
                            onChange={(e: any) => {
                                const val = e.target.value;
                                if (/^\d{0,2}$/.test(val) || val === '100') {
                                    handleChange('share', val);
                                }
                            }}
                            onBlur={() => onShareBlur(index)}
                            maxLength={3}
                        />
                        <span className="percent-symbol">%</span>
                    </div>
                    {showExceedsError && (
                        <span className="share-exceeds-error">Exceeds 100%</span>
                    )}
                    {!nominee?.isShareTouched && !showExceedsError && (
                        <span className="share-auto-hint">Auto — tap to edit</span>
                    )}
                </div>
            </div>

            {/* Minor checkbox row - on mobile: minor checkbox (50%) + share (50%) */}
            <div className="nominee-minor-row">
                <div className="nominee-minor-checkbox-new">
                    <CheckBoxField
                        label={dashboard.UpdateNominee.isMinorLabel}
                        value={isMinor}
                        onChange={(e: any) => handleChange('isMinor', e.target.checked)}
                    />
                </div>

                {/* DOB field - shows inline on desktop when minor is checked */}
                {isMinor && (
                    <div
                        className="nominee-minor-dob nominee-minor-dob-desktop"
                        data-nominee-index={index}
                        data-field-name="dob"
                    >
                        <label className="nominee-field-label-new">
                            Date of birth <span className="required-star">*</span>
                        </label>
                        <DatePickerInput
                            id={`nominee-dob-${index}`}
                            value={nominee.dob || null}
                            onChange={(date: any) => handleChange('dob', date)}
                            minDate={_minDateForCalender()}
                            error={!!errors.dob}
                            helperText={errors.dob}
                        />
                    </div>
                )}

                {/* Share % - shown here only on mobile */}
                <div
                    className="nominee-field-share-mobile"
                    data-nominee-index={index}
                    data-field-name="share"
                >
                    <div className="share-header">
                        <label className="nominee-field-label-new">
                            {dashboard.UpdateNominee.sharePercent}
                        </label>
                    </div>
                    <div className={`share-input-wrapper ${showExceedsError ? 'has-error' : ''}`}>
                        <input
                            type="text"
                            className="share-input"
                            value={nominee.share !== null && nominee.share !== undefined ? nominee.share : ''}
                            onChange={(e: any) => {
                                const val = e.target.value;
                                if (/^\d{0,2}$/.test(val) || val === '100') {
                                    handleChange('share', val);
                                }
                            }}
                            onBlur={() => onShareBlur(index)}
                            maxLength={3}
                        />
                        <span className="percent-symbol">%</span>
                    </div>
                    {showExceedsError && (
                        <span className="share-exceeds-error">Exceeds 100%</span>
                    )}
                    {!nominee?.isShareTouched && !showExceedsError && (
                        <span className="share-auto-hint">Auto — tap to edit</span>
                    )}
                </div>

                {/* Remove button - desktop only in this row */}
                {totalNominees > 1 && index > 0 && (
                    <button
                        type="button"
                        className="nominee-remove-btn nominee-remove-btn-desktop"
                        onClick={() => handleRemoveNominee(index)}
                    >
                        <img src="/images/delete_nominee.svg" alt="remove" />
                        <span>Nom-{index + 1}</span>
                    </button>
                )}
            </div>

            {/* DOB field - shown on separate row on mobile when minor is checked */}
            {isMinor && (
                <div className="nominee-dob-row">
                    <div
                        className="nominee-minor-dob"
                        data-nominee-index={index}
                        data-field-name="dob"
                    >
                        <label className="nominee-field-label-new">
                            Date of birth <span className="required-star">*</span>
                        </label>
                        <DatePickerInput
                            id={`nominee-dob-mobile-${index}`}
                            value={nominee.dob || null}
                            onChange={(date: any) => handleChange('dob', date)}
                            minDate={_minDateForCalender()}
                            error={!!errors.dob}
                            helperText={errors.dob}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}

export default NomineeCard;
